import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-4ffde150"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "highlight-settings-layout" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "labels-column" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "function-label" }, "Show"),
  /*#__PURE__*/_createElementVNode("span", { class: "function-label" }, "Color"),
  /*#__PURE__*/_createElementVNode("span", { class: "function-label" }, "Bright")
], -1))
const _hoisted_3 = { class: "controls-columns" }
const _hoisted_4 = ["onMouseenter", "onMouseleave"]
const _hoisted_5 = ["onClick", "onMouseenter"]
const _hoisted_6 = ["tabindex", "onClick", "onMouseenter"]
const _hoisted_7 = ["viewBox"]
const _hoisted_8 = ["d", "fill-rule"]
const _hoisted_9 = ["tabindex", "onClick", "onMouseenter"]
const _hoisted_10 = ["viewBox"]
const _hoisted_11 = ["d", "fill-rule"]

import { watch, onMounted, onUnmounted, ref } from "vue";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";
import { getSvgPath } from "@/utils/svgPaths";
import { noteNames } from "@/utils/noteUtils";
import ElementTooltip from "./ElementTooltip.vue";
import NoteLabel from "./NoteLabel.vue";
import ScaleDegreeLabel from "./ScaleDegreeLabel.vue";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";

// Global tooltip coordination

export default /*#__PURE__*/_defineComponent({
  __name: 'HighlightSettings',
  setup(__props) {

const {
  registerTooltip,
  unregisterTooltip,
  showTooltip: showGlobalTooltip,
  hideTooltip: hideGlobalTooltip,
} = useGlobalTooltip();

// Column-based tooltip state
const columnTooltip = ref({
  visible: false,
  content: "",
  targetElement: null as HTMLElement | null,
  currentColumn: -1,
});

const store = useFretboardStore();
const {
  scaleDegreeSettings,
  rootNote,
  isScaleDegree,
  selectedScale,
  isSettingsCollapsed,
} = storeToRefs(store);

// Use tooltip functions from props (access directly to maintain reactivity)

function getNoteColor(index: number) {
  const setting = scaleDegreeSettings.value[index];

  if (!setting.color) {
    // Show buttons: bright = shade-60 bg, dim = shade-30 bg
    return setting.bright ? "var(--shade-60)" : "var(--shade-30)";
  }

  // index now represents chromatic interval directly
  const colorIndex = index % 12;
  return setting.bright
    ? `var(--color-${colorIndex})`
    : `var(--color-${colorIndex}-dimmed)`;
}

function getTextColor(index: number) {
  const setting = scaleDegreeSettings.value[index];

  // Show buttons OFF: shade-40
  if (!setting.show) return "var(--shade-40)";

  if (!setting.color) {
    // Show buttons ON + brightness OFF: shade-60 text
    // Show buttons ON + brightness ON: shade-10 text
    return setting.bright ? "var(--shade-10)" : "var(--shade-60)";
  }

  // When color is enabled, use dark text on bright colors, light text on dim colors
  return setting.bright ? "var(--shade-20)" : "var(--light-48)";
}

function getColorButtonColor(index: number) {
  const setting = scaleDegreeSettings.value[index];
  if (!setting.color) {
    return "var(--shade-40)";
  }
  // Always use bright color for color button icons for better contrast
  const colorIndex = index % 12;
  return `var(--color-${colorIndex})`;
}

function getDimColor(index: number) {
  const setting = scaleDegreeSettings.value[index];
  if (!setting.color) {
    // When colors are OFF: bright buttons should be shade-60, dimmed buttons shade-40
    return setting.bright ? "var(--shade-60)" : "var(--shade-40)";
  }
  // When colors are ON: active dim buttons shade-60, inactive dim buttons shade-40
  return setting.bright ? "var(--shade-60)" : "var(--shade-40)";

  // Alternative colored approach (may want to return to this):
  // const colorIndex = index % 12;
  // return setting.bright
  //   ? `var(--color-${colorIndex})`
  //   : `var(--color-${colorIndex}-dimmed)`;
}

function toggleShow(index: number) {
  const currentSettings = [...scaleDegreeSettings.value];
  currentSettings[index] = {
    ...currentSettings[index],
    show: !currentSettings[index].show,
  };
  store.setScaleDegreeSettings(currentSettings);
  store.switchToCustomMode(); // Auto-switch dropdown to Custom (label only)

  // Update tooltip content immediately if this column is active
  if (
    columnTooltip.value.currentColumn === index &&
    columnTooltip.value.visible
  ) {
    columnTooltip.value.content = currentSettings[index].show
      ? "Hide note"
      : "Show note";
  }
}

function toggleColor(index: number) {
  const currentSettings = [...scaleDegreeSettings.value];
  currentSettings[index] = {
    ...currentSettings[index],
    color: !currentSettings[index].color,
  };
  store.setScaleDegreeSettings(currentSettings);

  // Update tooltip content immediately if this column is active
  if (
    columnTooltip.value.currentColumn === index &&
    columnTooltip.value.visible
  ) {
    columnTooltip.value.content = currentSettings[index].color
      ? "Color off"
      : "Color on";
  }
}

function toggleBright(index: number) {
  const currentSettings = [...scaleDegreeSettings.value];
  currentSettings[index] = {
    ...currentSettings[index],
    bright: !currentSettings[index].bright,
  };
  store.setScaleDegreeSettings(currentSettings);

  // Update tooltip content immediately if this column is active
  if (
    columnTooltip.value.currentColumn === index &&
    columnTooltip.value.visible
  ) {
    columnTooltip.value.content = currentSettings[index].bright
      ? "Bright off"
      : "Bright on";
  }
}

watch(
  () => store.isScaleDegree,
  (newValue) => {
    isScaleDegree.value = newValue;
  }
);

// Note: Removed forced re-render - Vue's reactivity handles this automatically

// Column-based tooltip handlers
function handleColumnEnter(columnIndex: number) {
  columnTooltip.value.currentColumn = columnIndex;
}

function handleColumnLeave(columnIndex: number) {
  // Only hide if we're leaving the current column
  if (columnTooltip.value.currentColumn === columnIndex) {
    hideColumnTooltip();
    columnTooltip.value.currentColumn = -1;
  }
}

function handleButtonHover(
  columnIndex: number,
  _buttonType: "show" | "color" | "bright",
  content: string
) {
  // Only show tooltip if we're in the current column
  if (columnTooltip.value.currentColumn === columnIndex) {
    columnTooltip.value.content = content;
    columnTooltip.value.visible = true;
    showGlobalTooltip("highlight-settings");

    // Always position tooltip above the show button (first button in column)
    const showButton = document.querySelector(
      `.note-column:nth-child(${columnIndex + 1}) .show-button`
    ) as HTMLElement;
    if (showButton) {
      columnTooltip.value.targetElement = showButton;
    }
  }
}

function hideColumnTooltip() {
  columnTooltip.value.visible = false;
  hideGlobalTooltip("highlight-settings");
}

onMounted(() => {
  isScaleDegree.value = store.isScaleDegree;
  registerTooltip("highlight-settings", hideColumnTooltip);
});

onUnmounted(() => {
  unregisterTooltip("highlight-settings");
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["highlighting-settings", { 'highlighting-settings--collapsed': _unref(isSettingsCollapsed) }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _hoisted_2,
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(scaleDegreeSettings), (setting, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `column-${index}`,
            class: "note-column",
            onMouseenter: ($event: any) => (handleColumnEnter(index)),
            onMouseleave: ($event: any) => (handleColumnLeave(index))
          }, [
            _createElementVNode("button", {
              class: _normalizeClass(["show-button interactive-outline", {
              'show-button--visible': setting.show,
              'show-button--hidden': !setting.show,
              'show-button--colored': setting.color,
              'show-button--bright': setting.bright,
            }]),
              style: _normalizeStyle(
              setting.show ? { backgroundColor: getNoteColor(index) } : {}
            ),
              onClick: ($event: any) => (toggleShow(index)),
              onMouseenter: ($event: any) => (
              handleButtonHover(
                index,
                'show',
                setting.show ? 'Hide note' : 'Show note'
              )
            )
            }, [
              (!_unref(isScaleDegree))
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "button-note-label",
                    style: _normalizeStyle({
                  color: getTextColor(index),
                  fill: getTextColor(index),
                })
                  }, [
                    _createVNode(NoteLabel, {
                      "chromatic-index": (index + _unref(noteNames).indexOf(_unref(rootNote))) % 12,
                      "display-mode": 'logical',
                      "key-signature": _unref(rootNote),
                      size: "medium"
                    }, null, 8, ["chromatic-index", "key-signature"])
                  ], 4))
                : (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "button-note-label",
                    style: _normalizeStyle({
                  color: getTextColor(index),
                  fill: getTextColor(index),
                })
                  }, [
                    _createVNode(ScaleDegreeLabel, {
                      "chromatic-interval": index,
                      scale: _unref(selectedScale),
                      size: "medium"
                    }, null, 8, ["chromatic-interval", "scale"])
                  ], 4))
            ], 46, _hoisted_5),
            _createElementVNode("button", {
              class: _normalizeClass(["color-button interactive-outline", {
              'color-button--active': setting.color,
              'color-button--inactive': !setting.color,
              'color-button--hidden': !setting.show,
            }]),
              style: _normalizeStyle({
              fill: getColorButtonColor(index),
            }),
              tabindex: setting.show ? 0 : -1,
              onClick: ($event: any) => (toggleColor(index)),
              onMouseenter: ($event: any) => (
              handleButtonHover(
                index,
                'color',
                setting.color ? 'Color off' : 'Color on'
              )
            )
            }, [
              (_openBlock(), _createElementBlock("svg", {
                viewBox: _unref(getSvgPath)('colors')?.viewBox
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getSvgPath)('colors')?.paths, (path, pathIndex) => {
                  return (_openBlock(), _createElementBlock("path", {
                    key: `color-path-${pathIndex}`,
                    d: path.d,
                    "fill-rule": path.fillRule
                  }, null, 8, _hoisted_8))
                }), 128))
              ], 8, _hoisted_7))
            ], 46, _hoisted_6),
            _createElementVNode("button", {
              class: _normalizeClass(["dim-button interactive-outline", {
              'dim-button--bright': setting.bright,
              'dim-button--dimmed': !setting.bright,
              'dim-button--hidden': !setting.show,
            }]),
              style: _normalizeStyle({ fill: getDimColor(index) }),
              tabindex: setting.show ? 0 : -1,
              onClick: ($event: any) => (toggleBright(index)),
              onMouseenter: ($event: any) => (
              handleButtonHover(
                index,
                'bright',
                setting.bright ? 'Bright off' : 'Bright on'
              )
            )
            }, [
              (_openBlock(), _createElementBlock("svg", {
                viewBox: _unref(getSvgPath)('brightness')?.viewBox
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getSvgPath)('brightness')?.paths, (path, pathIndex) => {
                  return (_openBlock(), _createElementBlock("path", {
                    key: `brightness-path-${pathIndex}`,
                    d: path.d,
                    "fill-rule": path.fillRule
                  }, null, 8, _hoisted_11))
                }), 128))
              ], 8, _hoisted_10))
            ], 46, _hoisted_9)
          ], 40, _hoisted_4))
        }), 128))
      ])
    ]),
    _createVNode(ElementTooltip, {
      visible: columnTooltip.value.visible,
      content: columnTooltip.value.content,
      "target-element": columnTooltip.value.targetElement,
      position: 'above'
    }, null, 8, ["visible", "content", "target-element"])
  ], 2))
}
}

})